import type { UseQueryResult } from '@tanstack/react-query';
import type {
  PolicySettings,
  PolicySetting,
  PolicySettingsUpdate,
} from '@kirin/api-client/contracts/policies';

/**
 * Subset of TanStack Query result used by policy settings UIs (load, error, retry).
 * Matches {@link useUserPolicySettings} / {@link useOrganizationPolicySettings}.
 */
export type PolicySettingsQueryHookResult = Pick<
  UseQueryResult<PolicySettings>,
  'data' | 'isLoading' | 'isError' | 'error' | 'refetch'
>;

export type UsePolicySettingsHook = (
  policyKey: string | undefined,
  enabled?: boolean
) => PolicySettingsQueryHookResult;

/**
 * Type helper to extract the value type from a PolicySetting based on its type field.
 * This provides better type safety than `unknown`.
 */
type PolicySettingValueType<T extends PolicySetting['type']> =
  T extends 'boolean'
    ? boolean
    : T extends 'string'
      ? string
      : T extends 'array'
        ? unknown[] // Array values can be string[] or object[], handled at runtime
        : T extends 'object'
          ? Record<string, unknown>
          : unknown;

/**
 * Form data type derived from PolicySettings.
 * Maps each setting key to its appropriate value type based on the setting's type.
 * This is more type-safe than Record<string, unknown> while still being flexible
 * enough to handle dynamic policy settings.
 */
export type PolicySettingsFormData = {
  [K in string]?: PolicySettingValueType<PolicySetting['type']>;
};

/**
 * Type guard to check if a value is a boolean
 */
function isBoolean(value: unknown): value is boolean {
  return typeof value === 'boolean';
}

/**
 * Type guard to check if a value is a string
 */
function isString(value: unknown): value is string {
  return typeof value === 'string';
}

/**
 * Type guard to check if a value is an array
 */
function isArray(value: unknown): value is unknown[] {
  return Array.isArray(value);
}

/**
 * Type guard to check if a value is a plain object
 */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Helper to convert PolicySettings to form data.
 * Uses the actual value from settings or falls back to defaultValue.
 * Includes runtime type checking for better type safety.
 *
 * Special handling for cursor-database policy:
 * - Merges maliciousPatterns + credentialPatterns for display as code patterns
 * - This provides backward compatibility while simplifying the UI
 *
 * @param settings - The policy settings to convert
 * @returns Form data object with properly typed values
 */
export function policySettingsToFormData(
  settings: PolicySettings
): PolicySettingsFormData {
  const formData: PolicySettingsFormData = {};

  // Special handling for cursor-database: merge maliciousPatterns + credentialPatterns
  const isCursorDatabase = settings.policy.key === 'cursor-database';
  let maliciousPatterns: unknown[] = [];
  let credentialPatterns: unknown[] = [];

  settings.settings.forEach(setting => {
    // Use the value if present, otherwise use defaultValue
    const value = setting.value ?? setting.defaultValue;

    // Collect patterns for merging
    if (isCursorDatabase) {
      if (setting.key === 'maliciousPatterns') {
        maliciousPatterns = isArray(value) ? value : [];
        return; // Don't add to formData yet
      }
      if (setting.key === 'credentialPatterns') {
        credentialPatterns = isArray(value) ? value : [];
        return; // Don't add to formData, will merge into maliciousPatterns
      }
    }

    // Runtime type checking with proper type guards
    switch (setting.type) {
      case 'boolean':
        formData[setting.key] = isBoolean(value) ? value : false;
        break;
      case 'string':
        formData[setting.key] = isString(value) ? value : String(value ?? '');
        break;
      case 'array':
        formData[setting.key] = isArray(value) ? value : [];
        break;
      case 'object':
        formData[setting.key] = isPlainObject(value) ? value : {};
        break;
      default:
        formData[setting.key] = value;
    }
  });

  // Merge patterns for cursor-database
  if (isCursorDatabase) {
    formData['maliciousPatterns'] = [
      ...maliciousPatterns,
      ...credentialPatterns,
    ];
    formData['credentialPatterns'] = []; // Keep as empty array for form validation
  }

  return formData;
}

/**
 * Type helper to extract the settings type from PolicySettingsUpdate for a given policyKey.
 * This allows us to get the exact type-safe settings for a specific policy.
 */
export type SettingsForPolicy<T extends PolicySettingsUpdate['policyKey']> =
  Extract<PolicySettingsUpdate, { policyKey: T }>['settings'];
